/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { jsx } from '@emotion/react'

import type { EuiLinkAnchorProps, EuiThemeComputed } from '@elastic/eui'
import { EuiLink } from '@elastic/eui'

import { CuiSmallErrorBoundary } from '@modules/cui/SmallErrorBoundary'
import { useConfig } from '@modules/cui/ConfigContext'

import docLinks from './docLinks'

import type { Interpolation } from '@emotion/react'

export type DocLinkKey = keyof typeof docLinks

type Props = {
  showExternalLinkIcon?: true
  target?: EuiLinkAnchorProps['target']
  'aria-label'?: string
  'data-test-subj'?: string
  css?: EuiLinkAnchorProps['css']
}

export const DocLink: React.FunctionComponent<
  Props & { link: DocLinkKey; css?: Interpolation<EuiThemeComputed> }
> = ({ link: linkKey, ...rest }) => {
  const appType = useConfig('CLOUD_UI_APP')
  const appName = useConfig('APP_NAME')

  // Use the most specific link if available; e.g. saas-adminconsole > adminconsole.
  const href = docLinks[linkKey][appType] ?? docLinks[linkKey][appName]

  return <DocLinkRenderer href={href} {...rest} />
}

/** @deprecated Most consumers should use DocLink */
export const DocLinkRenderer: React.FunctionComponent<Props & { href: string | undefined }> = ({
  href,
  showExternalLinkIcon = false,
  target = '_blank',
  'aria-label': ariaLabel,
  'data-test-subj': dataTestSubj,
  children,
  ...rest
}) => (
  <CuiSmallErrorBoundary forInlineText={true}>
    <EuiLink
      href={href}
      className='docLink'
      external={showExternalLinkIcon}
      target={target}
      aria-label={ariaLabel}
      rel='noopener noreferrer'
      data-test-subj={dataTestSubj}
      css={{
        display: 'inline-flex',
        alignItems: 'center',
      }}
      {...rest}
    >
      {children}
    </EuiLink>
  </CuiSmallErrorBoundary>
)
